import { sd } from "@sd/lib/sdPaths";
import {
  TRACKER_REPORT_STATUS_COLUMNS,
  isTrackerOpenStatus,
  trackerClosedStatusIds,
  type TrackerReportRowId,
  type TrackerReportStatusId,
} from "@sd/lib/trackerReportConstants";
import {
  readTrackerReportProjectId,
  readTrackerReportReturnFilters,
} from "@sd/lib/ticketListDrillDownStorage";
import { sanitizeStatusFilterValues } from "@sd/constants/ticketStatuses";
import {
  parseStatusDashboardProjectParam,
} from "@sd/lib/statusDashboardApi";
import {
  appendTrackerReportFilterParams,
  mergeTrackerReportFilters,
  parseAssigneeFilterFromSearchParams,
  type TrackerReportFilters,
} from "@sd/lib/trackerReportFilters";
import {
  emptyCreatedDateFilter,
  normalizeCreatedDateFilter,
  type CreatedDateFilterState,
} from "@sd/lib/ticketCreatedDateFilter";
import { normalizeMultiFilterValues } from "@sd/lib/ticketFilterApi";

export type TrackerDrillDownColumn = "status" | "open" | "close" | "total";

export type TrackerReportPanel = "admin" | "agent";

export const TRACKER_REPORT_FROM_QUERY = "from";
export const TRACKER_REPORT_FROM_VALUE = "report";
export const TRACKER_REPORT_FROM_PROJECTS_VALUE = "projects";

export function trackerTicketsListPath(panel: TrackerReportPanel): string {
  return panel === "admin" ? sd("admin/tickets") : sd("tickets/all");
}

export function trackerReportListPath(panel: TrackerReportPanel): string {
  return panel === "admin" ? sd("admin/report") : sd("tickets/report");
}

export function isTrackerReportFromProjects(
  searchParams: URLSearchParams
): boolean {
  return (
    searchParams.get(TRACKER_REPORT_FROM_QUERY) ===
    TRACKER_REPORT_FROM_PROJECTS_VALUE
  );
}

/** Projects page click → tracker report for that project (filters hidden). */
export function buildProjectTrackerReportPath(
  projectId: string,
  panel: TrackerReportPanel = "admin"
): string {
  const id = projectId.trim();
  const params = new URLSearchParams();
  if (id) params.set("project", id);
  params.set(TRACKER_REPORT_FROM_QUERY, TRACKER_REPORT_FROM_PROJECTS_VALUE);
  return `${trackerReportListPath(panel)}?${params.toString()}`;
}

/** GET `/service_desk/ticket_create/` list filter `type` for a matrix row. */
export function trackerRowToListTypeFilter(rowId: TrackerReportRowId): string {
  if (rowId === "bug") return "issue";
  return rowId;
}

function openStatusIds(): TrackerReportStatusId[] {
  return TRACKER_REPORT_STATUS_COLUMNS.filter(col =>
    isTrackerOpenStatus(col.id)
  ).map(col => col.id);
}

export interface TrackerDrillDownInput {
  projectId?: string;
  projectFilters?: string[];
  assigneeFilter?: string;
  createdDateFilter?: CreatedDateFilterState;
  rowId?: TrackerReportRowId;
  column: TrackerDrillDownColumn;
  statusId?: TrackerReportStatusId;
}

function resolveTrackerProjectFilters(input: TrackerDrillDownInput): string[] {
  if (input.projectFilters?.length) {
    return normalizeMultiFilterValues(input.projectFilters);
  }
  const single = input.projectId?.trim();
  return single ? [single] : [];
}

/** Build ticket list URL with filters from a tracker matrix cell. */
export function buildTrackerDrillDownPath(
  input: TrackerDrillDownInput,
  panel: TrackerReportPanel = "admin"
): string {
  const params = new URLSearchParams();
  appendTrackerReportFilterParams(params, {
    projectFilters: resolveTrackerProjectFilters(input),
    assigneeFilter: input.assigneeFilter?.trim() || "all",
    createdDateFilter: normalizeCreatedDateFilter(
      input.createdDateFilter ?? emptyCreatedDateFilter()
    ),
  });
  params.set(TRACKER_REPORT_FROM_QUERY, TRACKER_REPORT_FROM_VALUE);

  if (input.rowId) {
    params.set("type", trackerRowToListTypeFilter(input.rowId));
  }

  if (input.column === "status" && input.statusId) {
    params.set("status", input.statusId);
  } else if (input.column === "open") {
    params.set("status", openStatusIds().join(","));
  } else if (input.column === "close") {
    params.set("status", trackerClosedStatusIds().join(","));
  }

  return `${trackerTicketsListPath(panel)}?${params.toString()}`;
}

/** Minimal ticket-list URL after drill-down filters are applied. */
export function buildTrackerReportReturnSearchParams(): URLSearchParams {
  const params = new URLSearchParams();
  params.set(TRACKER_REPORT_FROM_QUERY, TRACKER_REPORT_FROM_VALUE);
  return params;
}

/** Ticket list → Report with filters pre-selected. */
export function buildTrackerReportReturnPath(
  filters: TrackerReportFilters | string | string[],
  panel: TrackerReportPanel = "admin"
): string {
  const params = new URLSearchParams();
  const resolved =
    typeof filters === "string"
      ? mergeTrackerReportFilters({
          projectFilters: filters.trim() ? [filters.trim()] : [],
        })
      : Array.isArray(filters)
        ? mergeTrackerReportFilters({ projectFilters: filters })
        : mergeTrackerReportFilters(filters);
  appendTrackerReportFilterParams(params, resolved);
  const query = params.toString();
  const base = trackerReportListPath(panel);
  return query ? `${base}?${query}` : base;
}

/** Restore tracker report filters from page URL query params. */
export function parseTrackerReportPageSearchParams(
  searchParams: URLSearchParams
): TrackerReportFilters | null {
  const project = searchParams.get("project")?.trim();
  const startDate = searchParams.get("start_date")?.trim();
  const endDate = searchParams.get("end_date")?.trim();
  const assigneeFilter = parseAssigneeFilterFromSearchParams(searchParams);
  const projectFilters = project ? parseStatusDashboardProjectParam(project) : [];

  if (
    projectFilters.length === 0 &&
    !startDate &&
    !endDate &&
    assigneeFilter === "all"
  ) {
    return null;
  }

  return mergeTrackerReportFilters({
    projectFilters,
    assigneeFilter,
    createdDateFilter: normalizeCreatedDateFilter({
      from: startDate ?? "",
      to: endDate ?? "",
    }),
  });
}

export function isTrackerReportReturnContext(searchParams: URLSearchParams): {
  projectId: string;
} | null {
  if (searchParams.get(TRACKER_REPORT_FROM_QUERY) !== TRACKER_REPORT_FROM_VALUE) {
    return null;
  }
  const projectId =
    searchParams.get("project")?.trim() || readTrackerReportProjectId();
  return { projectId };
}

export function parseTrackerDrillDownSearchParams(
  searchParams: URLSearchParams
): {
  projectFilters: string[];
  statusFilters: string[];
  typeFilter: string;
  assigneeFilter: string;
  createdDateFilter: CreatedDateFilterState;
} | null {
  const project = searchParams.get("project")?.trim();
  const status = searchParams.get("status")?.trim();
  const type = searchParams.get("type")?.trim();
  const startDate = searchParams.get("start_date")?.trim();
  const endDate = searchParams.get("end_date")?.trim();
  const fromReport =
    searchParams.get(TRACKER_REPORT_FROM_QUERY) === TRACKER_REPORT_FROM_VALUE;

  // `from=report&project=…` alone is back-context, not a new drill-down.
  if (!status && !type) return null;
  if (!project && !status && !type) return null;

  // Status-only URLs without report drill-down are toolbar filters, not matrix drill-down.
  if (status && !type && !fromReport) return null;

  return {
    projectFilters: project ? parseStatusDashboardProjectParam(project) : [],
    statusFilters: sanitizeStatusFilterValues(
      status
        ? status
            .split(",")
            .map(s => s.trim())
            .filter(Boolean)
        : []
    ),
    typeFilter: type || "all",
    assigneeFilter: parseAssigneeFilterFromSearchParams(searchParams),
    createdDateFilter: normalizeCreatedDateFilter({
      from: startDate ?? "",
      to: endDate ?? "",
    }),
  };
}

export function readTrackerReportReturnFiltersFromStorage(): TrackerReportFilters {
  return readTrackerReportReturnFilters() ?? mergeTrackerReportFilters(null);
}
